import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import CTABlock from "../components/CTABlock";
import QuoteMiniForm from "../components/QuoteMiniForm";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { GARMENT_TYPES, GARMENT_DETAILS, FABRICS, waUrl, waCategoryMsg } from "../config/site";

export default function GarmentDetail() {
  const { slug } = useParams();
  const base = GARMENT_TYPES.find((g) => g.slug === slug);
  const detail = GARMENT_DETAILS[slug];

  if (!base || !detail) {
    return (
      <section className="container-site py-24 text-center" data-testid="garment-not-found">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Garment not found</h1>
        <p className="mt-3 text-obsidian/60">This garment category does not exist.</p>
        <Link to="/uniforms" className="btn-primary mt-8">Browse Uniforms</Link>
      </section>
    );
  }

  const fabrics = detail.fabrics.map((f) => FABRICS.find((x) => x.slug === f)).filter(Boolean);
  const related = GARMENT_TYPES.filter((g) => g.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO
        title={`${base.name} Manufacturer in India | Bulk Orders — Cleef Empire`}
        description={detail.seoDesc}
        path={`/uniforms/${slug}`}
      />

      <section className="border-b border-obsidian/10 bg-bone" data-testid="garment-hero">
        <div className="container-site grid items-center gap-10 py-14 lg:grid-cols-2 lg:gap-16 lg:py-20">
          <Reveal>
            <Link to="/uniforms" className="inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-obsidian/50 transition-colors hover:text-cleef" data-testid="garment-back-link">
              <ArrowLeft className="h-3.5 w-3.5" /> All Uniforms
            </Link>
            <p className="eyebrow mt-6">Garment Program</p>
            <h1 className="mt-3 font-display text-4xl font-black leading-[1.06] tracking-tight sm:text-5xl">{base.name}</h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-obsidian/60">{detail.description}</p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {base.items.map((item) => (
                <li key={item} className="border border-obsidian/15 bg-white px-3.5 py-1.5 text-xs font-medium text-obsidian/75">{item}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href="#garment-quote" data-event="bulk_quote_click" data-testid="garment-hero-quote-btn" className="btn-primary">
                Get Bulk Quote <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={waUrl(waCategoryMsg(base.name.toLowerCase()))}
                target="_blank"
                rel="noopener noreferrer"
                data-event="whatsapp_click"
                data-testid="garment-hero-whatsapp-btn"
                className="btn-outline"
              >
                <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> WhatsApp
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="frame-offset overflow-hidden">
              <img src={detail.img} alt={detail.alt} className="aspect-[4/3] w-full object-cover" loading="eager" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container-site py-16 lg:py-20" data-testid="garment-fabrics">
        <Reveal>
          <p className="eyebrow">Fabric Options</p>
          <h2 className="mt-3 max-w-2xl font-display text-2xl font-extrabold leading-[1.1] tracking-tight sm:text-3xl">
            Recommended fabrics for {base.name.toLowerCase()}
          </h2>
          <p className="mt-3 max-w-xl text-sm text-obsidian/55">
            Final fabric and GSM are confirmed per product specification during development.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-px border border-obsidian/10 bg-obsidian/10 sm:grid-cols-2">
          {fabrics.map((f, i) => (
            <Reveal key={f.slug} delay={i * 0.08} className="bg-white">
              <Link to={`/fabrics#${f.slug}`} className="group flex h-full gap-5 p-6" data-testid={`garment-fabric-${f.slug}`}>
                <img src={f.img} alt={f.alt} loading="lazy" className="h-24 w-24 shrink-0 object-cover" />
                <span>
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-cleef">{f.gsm}</span>
                  <span className="mt-1 block font-display text-lg font-bold tracking-tight group-hover:text-cleef">{f.name}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-obsidian/55">{f.desc}</span>
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-obsidian/10 bg-white" data-testid="garment-construction">
        <div className="container-site grid gap-12 py-16 lg:grid-cols-2 lg:py-20">
          <Reveal>
            <p className="eyebrow">Construction & Customization</p>
            <h2 className="mt-3 font-display text-2xl font-extrabold leading-[1.1] tracking-tight sm:text-3xl">
              Built to your specification
            </h2>
            <ul className="mt-8 space-y-4">
              {detail.construction.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-obsidian/75">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border border-cleef/30 bg-cleef-light">
                    <Check className="h-3 w-3 text-cleef" />
                  </span>
                  {c}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs leading-relaxed text-obsidian/45">
              Options are available depending on product specification and are confirmed during development.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="scroll-mt-28">
            <div id="garment-quote">
              <QuoteMiniForm productType={base.name} testId={`garment-quote-form-${slug}`} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container-site py-16 lg:py-20" data-testid="garment-related">
        <Reveal>
          <h2 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Also manufactured in bulk</h2>
        </Reveal>
        <div className="mt-8 grid gap-px border border-obsidian/10 bg-obsidian/10 sm:grid-cols-3">
          {related.map((g, i) => (
            <Reveal key={g.slug} delay={i * 0.08} className="bg-ivory">
              <Link to={`/uniforms/${g.slug}`} className="group block h-full p-7" data-testid={`garment-related-${g.slug}`}>
                <h3 className="font-display text-lg font-bold tracking-tight group-hover:text-cleef">{g.name}</h3>
                <p className="mt-1.5 text-sm text-obsidian/60">{g.line}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-obsidian/60 group-hover:text-cleef">
                  View garment <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
      <CTABlock />
    </>
  );
}
